"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Ban, RotateCcw } from "lucide-react";
import { catalogApi, type Faculty, type Major } from "@/lib/catalog-api.service";

const BOARD_FACULTY_CODE = "Board";
import { Toast } from "@/lib/utils";

/** The system row that means "unrestricted visibility" — see catalog service. */

function sortCatalog<T extends { order: number; name: string }>(rows: T[]): T[] {
	return [...rows].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}

/**
 * Admin editor for the khoa and ngành catalogs: create, rename, reorder and
 * deactivate both, with the ngành table filtered to the selected khoa.
 * `Board` is the system row the visibility model depends on — the API
 * refuses to delete or deactivate it, and this UI does not offer the
 * controls that would trigger that refusal.
 */
export default function FacultyMajorManagement() {
	const [faculties, setFaculties] = useState<Faculty[]>([]);
	const [facultiesLoading, setFacultiesLoading] = useState(true);
	const [facultiesError, setFacultiesError] = useState<string | null>(null);
	const [selectedFacultyCode, setSelectedFacultyCode] = useState<string | null>(null);

	const [majors, setMajors] = useState<Major[]>([]);
	const [majorsLoading, setMajorsLoading] = useState(false);
	const [majorsError, setMajorsError] = useState<string | null>(null);

	const [creatingFaculty, setCreatingFaculty] = useState(false);
	const [facultyForm, setFacultyForm] = useState({ code: "", name: "" });
	const [facultyFormError, setFacultyFormError] = useState<string | null>(null);

	const [creatingMajor, setCreatingMajor] = useState(false);
	const [majorForm, setMajorForm] = useState({ code: "", name: "" });
	const [majorFormError, setMajorFormError] = useState<string | null>(null);

	const loadFaculties = useCallback(async () => {
		setFacultiesLoading(true);
		setFacultiesError(null);
		try {
			const data = await catalogApi.listFaculties(true);
			setFaculties(sortCatalog(data));
		} catch (error) {
			setFacultiesError(error instanceof Error ? error.message : "Không thể tải danh sách khoa");
		} finally {
			setFacultiesLoading(false);
		}
	}, []);

	const loadMajors = useCallback(async (facultyCode: string) => {
		setMajorsLoading(true);
		setMajorsError(null);
		try {
			const data = await catalogApi.listMajors({ facultyCode, includeInactive: true });
			setMajors(sortCatalog(data));
		} catch (error) {
			setMajorsError(error instanceof Error ? error.message : "Không thể tải danh sách ngành");
		} finally {
			setMajorsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadFaculties();
	}, [loadFaculties]);

	useEffect(() => {
		if (selectedFacultyCode) {
			loadMajors(selectedFacultyCode);
		} else {
			setMajors([]);
		}
	}, [selectedFacultyCode, loadMajors]);

	const selectedFaculty = useMemo(
		() => faculties.find((f) => f.code === selectedFacultyCode) ?? null,
		[faculties, selectedFacultyCode],
	);

	const handleCreateFaculty = async (event: React.FormEvent) => {
		event.preventDefault();
		setFacultyFormError(null);
		try {
			await catalogApi.createFaculty({
				code: facultyForm.code.trim(),
				name: facultyForm.name.trim(),
			});
			setFacultyForm({ code: "", name: "" });
			setCreatingFaculty(false);
			Toast.success("Đã thêm khoa");
			await loadFaculties();
		} catch (error) {
			setFacultyFormError(error instanceof Error ? error.message : "Không thể thêm khoa");
		}
	};

	const handleRenameFaculty = async (code: string, name: string) => {
		try {
			await catalogApi.updateFaculty(code, { name });
			await loadFaculties();
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể đổi tên khoa");
		}
	};

	const handleReorderFaculty = async (code: string, order: number) => {
		try {
			await catalogApi.updateFaculty(code, { order });
			await loadFaculties();
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể đổi thứ tự khoa");
		}
	};

	const handleToggleFacultyActive = async (code: string, active: boolean) => {
		try {
			if (active) {
				await catalogApi.updateFaculty(code, { active: true });
			} else {
				await catalogApi.deleteFaculty(code);
			}
			await loadFaculties();
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể cập nhật trạng thái khoa");
		}
	};

	const handleCreateMajor = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!selectedFacultyCode) return;
		setMajorFormError(null);
		try {
			await catalogApi.createMajor({
				code: majorForm.code.trim(),
				name: majorForm.name.trim(),
				facultyCode: selectedFacultyCode,
			});
			setMajorForm({ code: "", name: "" });
			setCreatingMajor(false);
			Toast.success("Đã thêm ngành");
			await loadMajors(selectedFacultyCode);
		} catch (error) {
			setMajorFormError(error instanceof Error ? error.message : "Không thể thêm ngành");
		}
	};

	const handleRenameMajor = async (code: string, name: string) => {
		if (!selectedFacultyCode) return;
		try {
			await catalogApi.updateMajor(code, { name });
			await loadMajors(selectedFacultyCode);
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể đổi tên ngành");
		}
	};

	const handleReorderMajor = async (code: string, order: number) => {
		if (!selectedFacultyCode) return;
		try {
			await catalogApi.updateMajor(code, { order });
			await loadMajors(selectedFacultyCode);
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể đổi thứ tự ngành");
		}
	};

	const handleToggleMajorActive = async (code: string, active: boolean) => {
		if (!selectedFacultyCode) return;
		try {
			if (active) {
				await catalogApi.updateMajor(code, { active: true });
			} else {
				await catalogApi.deleteMajor(code);
			}
			await loadMajors(selectedFacultyCode);
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể cập nhật trạng thái ngành");
		}
	};

	return (
		<div className="space-y-8">
			{/* Faculties (khoa) */}
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-semibold text-foreground">Danh mục khoa</h3>
					<Button
						size="sm"
						onClick={() => setCreatingFaculty((v) => !v)}
						className="flex items-center gap-2"
					>
						<Plus className="h-4 w-4" />
						Thêm khoa
					</Button>
				</div>

				{creatingFaculty && (
					<form onSubmit={handleCreateFaculty} className="flex items-end gap-2">
						<div>
							<label className="block text-sm font-medium text-muted-foreground mb-1">
								Mã khoa
							</label>
							<Input
								value={facultyForm.code}
								onChange={(e) => setFacultyForm((f) => ({ ...f, code: e.target.value }))}
								placeholder="VD: CNTT"
								required
							/>
						</div>
						<div className="flex-1">
							<label className="block text-sm font-medium text-muted-foreground mb-1">
								Tên khoa
							</label>
							<Input
								value={facultyForm.name}
								onChange={(e) => setFacultyForm((f) => ({ ...f, name: e.target.value }))}
								placeholder="Nhập tên khoa"
								required
							/>
						</div>
						<Button type="submit">Lưu</Button>
						<Button type="button" variant="outline" onClick={() => setCreatingFaculty(false)}>
							Hủy
						</Button>
					</form>
				)}
				{facultyFormError && (
					<p className="text-sm text-red-600 dark:text-red-400">{facultyFormError}</p>
				)}

				{facultiesError && (
					<p className="text-sm text-red-600 dark:text-red-400">{facultiesError}</p>
				)}
				{facultiesLoading ? (
					<div className="space-y-2">
						{Array.from({ length: 5 }).map((_, i) => (
							<Card key={i}>
								<CardContent className="p-3 flex items-center gap-3">
									<Skeleton className="h-5 w-16 rounded-full" />
									<Skeleton className="h-5 flex-1" />
								</CardContent>
							</Card>
						))}
					</div>
				) : (
					<div className="space-y-2">
						{faculties.map((faculty) => {
							const isBoard = faculty.code === BOARD_FACULTY_CODE;
							return (
								<Card
									key={faculty.code}
									className={`cursor-pointer ${!faculty.active ? "opacity-50 bg-muted" : ""} ${
										selectedFacultyCode === faculty.code ? "ring-2 ring-ring" : ""
									}`}
									onClick={() => setSelectedFacultyCode(faculty.code)}
								>
									<CardContent className="p-3 flex items-center gap-3">
										<Badge variant="outline">{faculty.code}</Badge>
										<Input
											defaultValue={faculty.name}
											className="flex-1"
											disabled={!faculty.active}
											onClick={(e) => e.stopPropagation()}
											onBlur={(e) => {
												const name = e.target.value.trim();
												if (name && name !== faculty.name) handleRenameFaculty(faculty.code, name);
											}}
										/>
										<Input
											type="number"
											defaultValue={faculty.order}
											className="w-20"
											disabled={!faculty.active}
											aria-label={`Thứ tự ${faculty.name}`}
											onClick={(e) => e.stopPropagation()}
											onBlur={(e) => {
												const order = Number(e.target.value);
												if (!Number.isNaN(order) && order !== faculty.order) {
													handleReorderFaculty(faculty.code, order);
												}
											}}
										/>
										{!faculty.active && <Badge variant="secondary">Đã vô hiệu hóa</Badge>}
										{/* Board is the system "unrestricted visibility" row — the API refuses
											to delete or deactivate it, so those controls are not offered. */}
										{/* No size="sm": these sit on a row with h-9 Inputs. */}
										{!isBoard &&
											(faculty.active ? (
												<Button
													variant="outline"
													onClick={(e) => {
														e.stopPropagation();
														handleToggleFacultyActive(faculty.code, false);
													}}
													className="flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-400"
												>
													<Ban className="h-3 w-3" />
													Vô hiệu hóa
												</Button>
											) : (
												<Button
													variant="outline"
													onClick={(e) => {
														e.stopPropagation();
														handleToggleFacultyActive(faculty.code, true);
													}}
													className="flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-400"
												>
													<RotateCcw className="h-3 w-3" />
													Kích hoạt
												</Button>
											))}
									</CardContent>
								</Card>
							);
						})}
					</div>
				)}
			</div>

			{/* Majors (ngành), filtered by the selected khoa */}
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-semibold text-foreground">
						Danh mục ngành{selectedFaculty ? ` — ${selectedFaculty.name}` : ""}
					</h3>
					<Button
						size="sm"
						disabled={!selectedFacultyCode}
						onClick={() => setCreatingMajor((v) => !v)}
						className="flex items-center gap-2"
					>
						<Plus className="h-4 w-4" />
						Thêm ngành
					</Button>
				</div>

				{!selectedFacultyCode ? (
					<p className="text-sm text-muted-foreground">Chọn một khoa để xem danh mục ngành</p>
				) : (
					<>
						{creatingMajor && (
							<form onSubmit={handleCreateMajor} className="flex items-end gap-2">
								<div>
									<label className="block text-sm font-medium text-muted-foreground mb-1">
										Mã ngành
									</label>
									<Input
										value={majorForm.code}
										onChange={(e) => setMajorForm((f) => ({ ...f, code: e.target.value }))}
										placeholder="VD: CNTT01"
										required
									/>
								</div>
								<div className="flex-1">
									<label className="block text-sm font-medium text-muted-foreground mb-1">
										Tên ngành
									</label>
									<Input
										value={majorForm.name}
										onChange={(e) => setMajorForm((f) => ({ ...f, name: e.target.value }))}
										placeholder="Nhập tên ngành"
										required
									/>
								</div>
								<Button type="submit">Lưu</Button>
								<Button type="button" variant="outline" onClick={() => setCreatingMajor(false)}>
									Hủy
								</Button>
							</form>
						)}
						{majorFormError && (
							<p className="text-sm text-red-600 dark:text-red-400">{majorFormError}</p>
						)}

						{majorsError && <p className="text-sm text-red-600 dark:text-red-400">{majorsError}</p>}
						{majorsLoading ? (
							<div className="space-y-2">
								{Array.from({ length: 4 }).map((_, i) => (
									<Card key={i}>
										<CardContent className="p-3 flex items-center gap-3">
											<Skeleton className="h-5 w-16 rounded-full" />
											<Skeleton className="h-9 flex-1" />
										</CardContent>
									</Card>
								))}
							</div>
						) : (
							<div className="space-y-2">
								{majors.map((major) => (
									<Card key={major.code} className={!major.active ? "opacity-50 bg-muted" : ""}>
										<CardContent className="p-3 flex items-center gap-3">
											<Badge variant="outline">{major.code}</Badge>
											<Input
												defaultValue={major.name}
												className="flex-1"
												disabled={!major.active}
												onBlur={(e) => {
													const name = e.target.value.trim();
													if (name && name !== major.name) handleRenameMajor(major.code, name);
												}}
											/>
											<Input
												type="number"
												defaultValue={major.order}
												className="w-20"
												disabled={!major.active}
												aria-label={`Thứ tự ${major.name}`}
												onBlur={(e) => {
													const order = Number(e.target.value);
													if (!Number.isNaN(order) && order !== major.order) {
														handleReorderMajor(major.code, order);
													}
												}}
											/>
											{!major.active && <Badge variant="secondary">Đã vô hiệu hóa</Badge>}
											{/* No size="sm": these sit on a row with h-9 Inputs. */}
											{major.active ? (
												<Button
													variant="outline"
													onClick={() => handleToggleMajorActive(major.code, false)}
													className="flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-400"
												>
													<Ban className="h-3 w-3" />
													Vô hiệu hóa
												</Button>
											) : (
												<Button
													variant="outline"
													onClick={() => handleToggleMajorActive(major.code, true)}
													className="flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-400"
												>
													<RotateCcw className="h-3 w-3" />
													Kích hoạt
												</Button>
											)}
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
